import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { Plus, Pencil, Trash2, X, Image as ImageIcon, Truck } from "lucide-react";
import api, { formatApiError, formatNaira } from "@/lib/api";

const EMPTY = {
  name: "",
  price: 0,
  original_price: null,
  image: "",
  categories: [],
  subcategories: [],
  rating: 0,
  review_count: 0,
  sku: "",
  features: [],
  in_stock: true,
  sold_count: 0,
  available_count: 100,
  description: "",
};

const CATEGORIES = ["Accessories", "Clothing", "Bags"];

export default function AdminPage() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [shipping, setShipping] = useState(null);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => { loadProducts(); loadOrders(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      loadProducts();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-page">
      <p className="label-eyebrow mb-3">Admin</p>
      <h1 className="font-serif text-5xl tracking-tighter mb-10">Dashboard</h1>

      <div className="flex gap-2 mb-10" data-testid="admin-tabs">
        {[{ k: "products", l: "Products" }, { k: "orders", l: "Orders" }].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`admin-tab-${t.k}`}
            className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-all ${
              tab === t.k ? "bg-[#333E48] text-white border-[#333E48]" : "border-[#E8E2D9]"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <>
          <div className="flex justify-end mb-6">
            <button onClick={() => setEditing(EMPTY)} className="btn-gold" data-testid="admin-add-product-btn">
              <Plus size={16} /> Add Product
            </button>
          </div>

          <div className="border border-[#E8E2D9] overflow-x-auto" data-testid="admin-products-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F3EFEA] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Image</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">SKU</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-[#E8E2D9]">
                    <td className="px-4 py-3"><img src={p.image} alt="" className="w-12 h-12 object-cover bg-[#F3EFEA]" /></td>
                    <td className="px-4 py-3 font-medium">{p.name}</td>
                    <td className="px-4 py-3 text-[#6A747D]">{(p.categories || []).join(", ")}</td>
                    <td className="px-4 py-3">{formatNaira(p.price)}</td>
                    <td className="px-4 py-3 text-[#6A747D]">{p.sku}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(p)} className="p-2 hover:bg-[#F3EFEA]" data-testid={`admin-edit-${p.id}`}><Pencil size={14} /></button>
                        <button onClick={() => remove(p.id)} className="p-2 hover:bg-[#F3EFEA] text-red-600" data-testid={`admin-delete-${p.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="border border-[#E8E2D9] overflow-x-auto" data-testid="admin-orders-table">
          {orders.length === 0 ? (
            <p className="p-12 text-center text-[#6A747D]">No orders yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F3EFEA] text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Order</th>
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Total</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-t border-[#E8E2D9]">
                    <td className="px-4 py-3 font-medium">{o.id}</td>
                    <td className="px-4 py-3 text-[#6A747D]">{o.user_email}</td>
                    <td className="px-4 py-3">{formatNaira(o.total)}</td>
                    <td className="px-4 py-3 capitalize">
                      <span className={`px-2 py-0.5 text-xs uppercase tracking-wider ${
                        o.status === "paid" ? "bg-[#FED700]/30" :
                        o.status === "shipped" ? "bg-emerald-100 text-emerald-700" :
                        "bg-[#F3EFEA]"
                      }`}>{o.status}</span>
                      {o.tracking_id && <p className="text-[10px] text-[#6A747D] mt-1">📦 {o.tracking_id}</p>}
                    </td>
                    <td className="px-4 py-3 text-[#6A747D]">{new Date(o.created_at).toLocaleString("en-NG")}</td>
                    <td className="px-4 py-3">
                      {o.status !== "shipped" && (
                        <button
                          onClick={() => setShipping(o)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#333E48] hover:bg-[#333E48] hover:text-white transition-colors"
                          data-testid={`admin-ship-${o.id}`}
                        >
                          <Truck size={12} /> Ship
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {editing && (
        <ProductDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadProducts(); }}
        />
      )}
      {shipping && (
        <ShipDialog
          order={shipping}
          onClose={() => setShipping(null)}
          onSaved={() => { setShipping(null); loadOrders(); }}
        />
      )}
    </div>
  );
}

function ShipDialog({ order, onClose, onSaved }) {
  const [tracking, setTracking] = useState("");
  const [courier, setCourier] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!tracking.trim()) { toast.error("Tracking ID is required"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/orders/${order.id}/ship`, { tracking_id: tracking.trim(), courier: courier.trim() || null });
      toast.success("Marked as shipped — customer notified");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-ship-dialog">
      <div className="bg-white max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-2xl">Ship order</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F3EFEA]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#6A747D] mb-6">Order <strong className="text-[#333E48]">{order.id}</strong> · {order.user_email}</p>
        <div className="space-y-5">
          <div>
            <label className="label-eyebrow block mb-2">Tracking ID</label>
            <input className="aj-input" value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. GIG-123456789" data-testid="ship-tracking-input" autoFocus />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Courier (optional)</label>
            <input className="aj-input" value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="e.g. GIG Logistics, DHL" data-testid="ship-courier-input" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-gold" data-testid="ship-submit-btn">{saving ? "Sending…" : "Ship & Notify"}</button>
        </div>
      </div>
    </div>
  );
}

function ProductDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...initial,
    features: (initial.features || []).join(", "),
    categories: (initial.categories || []).join(", "),
    subcategories: (initial.subcategories || []).join(", "),
    original_price: initial.original_price ?? "",
  });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        price: Number(form.price),
        original_price: form.original_price === "" ? null : Number(form.original_price),
        image: form.image,
        categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
        subcategories: form.subcategories.split(",").map((s) => s.trim()).filter(Boolean),
        rating: Number(form.rating) || 0,
        review_count: Number(form.review_count) || 0,
        sku: form.sku,
        features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
        in_stock: !!form.in_stock,
        sold_count: Number(form.sold_count) || 0,
        available_count: Number(form.available_count) || 0,
        description: form.description,
      };
      if (initial.id) {
        await api.put(`/products/${initial.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/products", payload);
        toast.success("Created");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="admin-product-dialog">
      <div className="bg-white max-w-2xl w-full my-8 p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-2xl">{initial.id ? "Edit product" : "New product"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F3EFEA]"><X size={20} /></button>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">Name</label>
            <input className="aj-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-form-name" />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Price (₦)</label>
            <input type="number" className="aj-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="admin-form-price" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Original Price (₦)</label>
            <input type="number" className="aj-input" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} data-testid="admin-form-original-price" />
          </div>

          <div>
            <label className="label-eyebrow block mb-2">SKU</label>
            <input className="aj-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="admin-form-sku" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Available</label>
            <input type="number" className="aj-input" value={form.available_count} onChange={(e) => setForm({ ...form, available_count: e.target.value })} />
          </div>

          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">Categories (comma-separated, suggested: {CATEGORIES.join(", ")})</label>
            <input className="aj-input" value={form.categories} onChange={(e) => setForm({ ...form, categories: e.target.value })} data-testid="admin-form-categories" />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">Subcategories</label>
            <input className="aj-input" value={form.subcategories} onChange={(e) => setForm({ ...form, subcategories: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">Features (comma-separated)</label>
            <input className="aj-input" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">Description</label>
            <textarea rows={3} className="aj-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-form-description" />
          </div>

          <div className="col-span-2">
            <label className="label-eyebrow block mb-2">Image</label>
            <div className="flex gap-3 items-start">
              {form.image ? (
                <img src={form.image} alt="" className="w-24 h-24 object-cover bg-[#F3EFEA]" />
              ) : (
                <div className="w-24 h-24 bg-[#F3EFEA] flex items-center justify-center text-[#6A747D]"><ImageIcon size={20} /></div>
              )}
              <div className="flex-1 space-y-2">
                <input className="aj-input" placeholder="Image URL or upload below" value={form.image?.startsWith("data:") ? "" : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} data-testid="admin-form-image-url" />
                <input type="file" accept="image/*" ref={fileRef} onChange={onFile} data-testid="admin-form-image-file" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="btn-outline">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-gold" data-testid="admin-form-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
